/*
 * X-QLearn-Entry: the coarse first touch of a new account, for the server's
 * once-per-account entry record (new_account_entries) and its log receipt
 * (growth_receipt event=new_account_entry).
 *
 * Carried once per account, on its first GET /qlearn/user/me: every sign-in
 * door looks the student up there right after sign-in (see account-created.js).
 * "Brand-new" is the server's word, never the browser's: the server reads the
 * account's creation time and records nothing for a returning account, so the
 * browser does not try to guess.
 *
 * The value is built from the stored FIRST touch only, as ";"-joined pairs,
 * each left out when it has nothing to say:
 *   source=…;medium=…;campaign=…  the touch's utm_source, utm_medium and
 *       utm_campaign. Each value must be [A-Za-z0-9_.-] and is capped at 64
 *       characters, the server's own rule (which it checks again); one value
 *       outside that drops all three, as the server would.
 *   click=1  Google/Microsoft ad click id present: the touch carried gclid,
 *       gbraid, wbraid or msclkid. Only the presence, never the value. Not
 *       fbclid, which Meta also appends to organic outbound links; a Meta ad
 *       click is identified by its utm medium instead.
 *   path=/<route>  the touch's landing route, reduced to its top-level route
 *       name from KNOWN_ENTRY_PATHS, or "other". The query, the fragment and
 *       every later segment are dropped, so a claim token
 *       (/notes-to-quiz/<token>), an exam id (/exam/<id>/…) or a QLink id
 *       never leaves the browser.
 * Never a click-id value, the referral code, the referrer or anything else in
 * the store. Every value is plain ASCII, which also keeps it safe for fetch
 * (it throws on a non-Latin-1 header value). A server that predates click and
 * path ignores them (it reads only the three utm keys), so the header stays
 * backward compatible.
 *
 * Once per account per device: a latch keyed by the session token's subject
 * (the account id), set when the first lookup is made, whether or not there
 * was anything to send, so concurrent and later lookups never repeat it. A
 * lookup that fails (network, 401, the server's profile gate 403 for a phone
 * account that has not finished its profile) never reached the server's
 * record, so the caller releases the latch and the next lookup carries the
 * entry. Sign-out's storage clear drops the latch; the server keeps its own
 * once-per-account record, keyed by the account id, inside its 24-hour
 * new-account window.
 *
 * Works on web and in the native app (the store is the same; native drops
 * `ref`, which is never read here). A server that does not know the header
 * ignores it (its CORS policy allows any request header). Never throws: it
 * sits on the sign-in path.
 */
import jwt_decode from "jwt-decode";
import { readStoredAttribution } from "./attribution";

export const ENTRY_HEADER = "X-QLearn-Entry";

const ENTRY_PARTS = [
  ["source", "utm_source"],
  ["medium", "utm_medium"],
  ["campaign", "utm_campaign"],
];
const VALID_VALUE = /^[A-Za-z0-9_.-]+$/;
const MAX_PART_LENGTH = 64;

// Google/Microsoft ad click id present: in the touch's params (gclid) or as
// the value-free clickId flag set at capture (gbraid, wbraid, msclkid are
// never stored). fbclid is deliberately absent: Meta appends it to organic
// outbound links, so it is not an ad signal.
export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "msclkid"];

// Top-level routes a first touch can land on. The server holds the same list
// (QLearn-service EntryHeader.KNOWN_PATHS); a route only one side knows is
// stored as "other".
export const KNOWN_ENTRY_PATHS = [
  "/",
  "/login", "/register", "/socialauth", "/forget-password", "/reset-password", "/verify-user",
  "/create-quiz", "/daily-absite-question", "/absite-diagnostic", "/absite-program-code-request",
  "/notes-to-quiz", "/question", "/qlink", "/live", "/live-session", "/exam", "/result", "/drive",
  "/gl", "/plans", "/manage-plans", "/subscriptions", "/payment", "/refund", "/quiz-history",
  "/overall-performance", "/user-settings", "/mentorships", "/program-metrics", "/feedback-dashboard",
  "/privacy-policy", "/terms", "/support", "/account-deleted", "/admin", "/admin-portal",
];
export const OTHER_ENTRY_PATH = "other";

export const entrySentKeyFor = (accountId) => `qlearn_entry_sent:${accountId}`;

const isPresent = (value) => value !== undefined && value !== null && String(value).trim() !== "";

// The three utm pairs, or none at all when one value is invalid.
const utmPairs = (params) => {
  if (!params || typeof params !== "object") return [];
  const pairs = [];
  for (const [name, key] of ENTRY_PARTS) {
    const value = isPresent(params[key]) ? String(params[key]).trim() : "";
    if (value) {
      if (!VALID_VALUE.test(value)) return [];
      pairs.push(`${name}=${value.slice(0, MAX_PART_LENGTH)}`);
    }
  }
  return pairs;
};

export const touchHasClickId = (touch) => {
  if (!touch || typeof touch !== "object") return false;
  if (touch.clickId === true) return true;
  const params = touch.params;
  return !!params && typeof params === "object" && CLICK_ID_KEYS.some((key) => isPresent(params[key]));
};

// "/<top-level route>" for a known route, "/" for the root, "other" for
// anything else, or undefined when the touch has no landing path.
export const entryPathOf = (landingPath) => {
  if (typeof landingPath !== "string" || !landingPath.startsWith("/")) return undefined;
  const pathname = landingPath.split(/[?#]/, 1)[0];
  const first = pathname.split("/").find((segment) => segment !== "");
  if (first === undefined) return "/";
  const route = `/${first.toLowerCase()}`;
  return KNOWN_ENTRY_PATHS.includes(route) ? route : OTHER_ENTRY_PATH;
};

export const buildEntryHeaderValue = (storedAttribution) => {
  const firstTouch = storedAttribution?.firstTouch;
  if (!firstTouch || typeof firstTouch !== "object") return undefined;

  const pairs = utmPairs(firstTouch.params);
  if (touchHasClickId(firstTouch)) pairs.push("click=1");
  const path = entryPathOf(firstTouch.landingPath);
  if (path) pairs.push(`path=${path}`);
  return pairs.length ? pairs.join(";") : undefined;
};

const accountIdFromToken = (token) => {
  try {
    const subject = token ? jwt_decode(token)?.sub : undefined;
    return typeof subject === "string" && subject ? subject : undefined;
  } catch (e) {
    return undefined;
  }
};

// For this account's first /user/me: { headers, release } (release() when
// the lookup failed), or undefined when there is nothing to send.
export const claimEntryHeader = (
  storage = typeof window !== "undefined" ? window.localStorage : undefined,
  readAttribution = readStoredAttribution
) => {
  try {
    const accountId = accountIdFromToken(storage?.getItem("token"));
    if (!accountId) return undefined;

    const sentKey = entrySentKeyFor(accountId);
    if (storage.getItem(sentKey)) return undefined;
    // Latched before anything is read, so a storage failure sends nothing
    // rather than sending on every lookup.
    storage.setItem(sentKey, "1");

    const value = buildEntryHeaderValue(readAttribution(storage));
    if (!value) return undefined;
    return {
      headers: { [ENTRY_HEADER]: value },
      release: () => {
        try {
          storage.removeItem(sentKey);
        } catch (e) {
          // Stays latched: the entry is lost, the sign-in is not.
        }
      },
    };
  } catch (e) {
    return undefined;
  }
};
